import {
  AfterViewInit,
  Component,
  ElementRef, EventEmitter,
  HostListener,
  Input,
  OnChanges, Output, Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {FormBuilder, FormGroup} from "@angular/forms";
import {PlatformControl} from "../../../../controls/core/platform/platform.control";
import {ButtonModule} from "../../../core/component/button/button.module";
import {CheckboxInputModule} from "../../../../components/common/checkbox/checkbox-input/checkbox-input.module";
import {NumberInputModule} from "../../../../components/common/number/number-input/number-input.module";
import {ColorPickerModule} from "ngx-color-picker";
import {CardModule} from "../../../core/component/card/card.module";
import {FormFieldModule} from "../../../core/component/form-field/form-field.module";
import {SummaryFieldModule} from "../../../core/component/summary-field/summary-field.module";
import {InputColorModule} from "../../../core/component/input-color/input-color.module";
import {SelectElementV2} from "../../../../components/common/select/selectElementV2";
import {SelectFilterV2Component} from "../../../../components/common/select-filter-v2/select-filter-v2.component";
import {MatTooltipModule} from "@angular/material/tooltip";
import {ExpandableContentModule} from "../../../core/component/expandable-content/expandable-content.module";
import {
  DrawingDataItem,
  DrawingData,
  DrawingDataItemCommon,
  DrawingDataItemDrawResult,
  DrawingElement,
  drawingElements
} from "./drawingOnImageDrawingElement.model";
import {
  DrawingOnImageItemSummaryFieldComponent
} from "../drawing-on-image-item-summary-field/drawing-on-image-item-summary-field.component";
import {InputTextModule} from "../../../core/component/input-text/input-text.module";
import {InputDrawingData} from "./drawingOnImageInputData";
import {DrawingDataItemBuilder} from "./items/drawingDataItemBuilder";


@Component({
  selector: 'app-drawing-on-image',
  standalone: true,
  imports: [CommonModule, ButtonModule, CheckboxInputModule, NumberInputModule, ColorPickerModule, CardModule, FormFieldModule, SummaryFieldModule, InputColorModule, SelectFilterV2Component, MatTooltipModule, ExpandableContentModule, DrawingOnImageItemSummaryFieldComponent, InputTextModule],
  templateUrl: './drawing-on-image.component.html',
  styles: [
  ]
})
export class DrawingOnImageComponent implements OnChanges, AfterViewInit{

  @Input('drawingData') inputModelingData: InputDrawingData;
  @Input() imageUrl: string;
  @Input() options: DrawingOptions;
  @Input() defaultColor = '#000000';
  @Output() changedEvent: EventEmitter<DrawingOnImageChangedEvent> = new EventEmitter();

  @ViewChild('drawingCanvas') drawingCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('backgroundCanvas') backgroundCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('parentDiv') parent!: ElementRef;

  elementsCardCollapsed = false;
  drawingData: DrawingData = {items: []}
  drawingElementsExpanded = true;
  isDrawing = false;
  formGroup: FormGroup;
  canvasMinimized = true;
  selectedElementValue = 'cross';
  selectedSubElementValue = undefined;
  selectedDrawingItemIndex = -1;
  optionElements: SelectElementV2<DrawingOption>[] = [];
  optionVertexElements: SelectElementV2<number> [] = [];
  drawingElements: DrawingElement[] = drawingElements;
  private backgroundCtx!: CanvasRenderingContext2D;
  private drawingCtx!: CanvasRenderingContext2D;
  zoom = 1;
  private translateX = 0;
  private translateY = 0;
  private isPanning = false;
  private panLastX = 0;
  private panLastY = 0;

  constructor(private formBuilder: FormBuilder,
              public platformControl: PlatformControl,
              private renderer: Renderer2) {
    this.formGroup = this.formBuilder.group({
      thickness: this.formBuilder.control(2),
      size: this.formBuilder.control(20),
      color: this.formBuilder.control('#000000'),
      label: this.formBuilder.control('Autre'),
      name: this.formBuilder.control('other'),
      optionValue: this.formBuilder.control('other'),
      vertexIndexToMove: this.formBuilder.control(-1),
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.imageUrl) {
      this.refreshBackgroundImage();
    }
    if (changes.options) {
      this.optionElements = [
        new SelectElementV2('other', 'Autres', undefined)
      ];
      if (this.options?.options) {
        this.options.options.forEach(value => this.optionElements.push(new SelectElementV2(value.name, value.label, value)));
      }
      this.optionSelected(this.optionElements[0]);
    }
    if (changes.inputModelingData) {
      this.isDrawing = false;
      this.selectedDrawingItemIndex = undefined;
      if (this.inputModelingData?.items) {
        this.drawingData.items = this.inputModelingData.items.map(value => {
          const item = DrawingDataItemBuilder.create(value.typeValue, value.subTypeValue);
          item.common = value.common;
          item.properties = value.properties;
          item.state = 'DONE';
          return item;
        });
        this.refresh();
        this.sendOnChangedEvent();
      }
    }
    if (changes.defaultColor && this.defaultColor) {
      this.formGroup.patchValue({
        color: this.defaultColor,
      })
    }
  }

  optionSelected(event: SelectElementV2<DrawingOption>) {
    this.adjustParentHeight();
    if (event && event.element) {
      this.selectedElementValue = event.element.element;
      this.selectedSubElementValue = event.element.subType;
    }

    this.drawingElementsExpanded = !event.element;
    if (event.element) {
      this.formGroup.patchValue({
        thickness: event.element.thickness,
        size: event.element.size,
        color: event.element.color,
        label: event.element.label,
        name: event.element.name,
      });
    } else {
      this.formGroup.patchValue({
        thickness: 2,
        color: '#000000',
        label: 'Autre',
        name: 'other',
      });
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustParentHeight();
  }

  ngAfterViewInit(): void {
    this.drawingCtx = this.drawingCanvasRef.nativeElement.getContext('2d')!;
    this.backgroundCtx = this.backgroundCanvasRef.nativeElement.getContext('2d');
    this.refreshBackgroundImage();
    setTimeout(() => {
      this.refresh();
      this.sendOnChangedEvent();
      this.applyZoom();
    }, 1000);

  }

  refreshBackgroundImage() {
    if (this.imageUrl) {
      const image = new Image();
      image.src = this.imageUrl;
      image.onload = () => {
        this.backgroundCanvasRef.nativeElement.width = image.width;
        this.backgroundCanvasRef.nativeElement.height = image.height;

        this.drawingCanvasRef.nativeElement.width = image.width;
        this.drawingCanvasRef.nativeElement.height = image.height;
        this.backgroundCtx.clearRect(0,0, this.backgroundCanvasRef.nativeElement.width, this.backgroundCanvasRef.nativeElement.height);
        this.backgroundCtx.drawImage(image, 0, 0);
        this.refresh();
        this.adjustParentHeightWithTimeout();
        this.applyZoom();
      };
    }
  }

  adjustParentHeightWithTimeout() {
    setTimeout(() => {
      this.adjustParentHeight();
    }, 500);
  }

  adjustParentHeight() {
    if (this.drawingCanvasRef && this.parent) {
      const childHeight = this.drawingCanvasRef.nativeElement.offsetHeight * this.zoom;
      this.renderer.setStyle(this.parent.nativeElement, 'height', `${childHeight}px`);
    }
  }

  public applyZoom() {
    if (this.drawingCanvasRef) {
      this.renderer.setStyle(
        this.drawingCanvasRef.nativeElement,
        'transform',
        `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoom})`
      );
      this.renderer.setStyle(this.drawingCanvasRef.nativeElement, 'transform-origin', '0 0');
    }
    if (this.backgroundCanvasRef) {
      this.renderer.setStyle(
        this.backgroundCanvasRef.nativeElement,
        'transform',
        `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoom})`
      );
      this.renderer.setStyle(this.backgroundCanvasRef.nativeElement, 'transform-origin', '0 0');
    }
    this.adjustParentHeight();
  }

  refreshOptions() {
    if (this.selectedDrawingItemIndex > -1) {
      this.drawingData.items[this.selectedDrawingItemIndex].common = this.buildCommonDrawingElementData();
      this.refresh();
      this.sendOnChangedEvent();
    }
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (event.shiftKey) {
      const rect = this.drawingCanvasRef.nativeElement.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;
      const oldZoom = this.zoom;
      const delta = event.deltaY;
      if (delta < 0) {
        this.zoom += 0.1;
      } else {
        this.zoom -= 0.1;
        if (this.zoom < 0.1) {
          this.zoom = 0.1;
        }
      }
      const scaleRatio = this.zoom / oldZoom;
      this.translateX -= offsetX * (scaleRatio - 1);
      this.translateY -= offsetY * (scaleRatio - 1);
      this.applyZoom();
      event.preventDefault();
    } else if (event.ctrlKey) {
      const delta = event.deltaY;
      let size = this.formGroup.get('size').value;
      if (delta < 0) {
        size += 20;
      } else {
        size -= 20;
      }
      if (size <= 0) {
        size = 1;
      }
      this.formGroup.get('size').setValue(size);
      this.refreshOptions();
      event.preventDefault();
    }
  }

  mouseDown(event: MouseEvent) {
    this.adjustParentHeight();
    if (event.button === 0 && this.zoom !== 1 && !this.isDrawing) {
      this.isPanning = true;
      this.panLastX = event.clientX;
      this.panLastY = event.clientY;
      event.preventDefault();
      return;
    }
    let newItem: DrawingDataItem;
    if (! this.isDrawing) {
      switch(this.selectedElementValue) {
        case 'line':
        case 'arrow':
        case 'columns':
        case 'rows':
        case 'angle':
        case 'shape':
          newItem = DrawingDataItemBuilder.create(this.selectedElementValue, this.selectedSubElementValue);
      }
    }

    if (newItem) {
      newItem.common = this.buildCommonDrawingElementData();
      this.drawingData.items.push(newItem);
      this.selectedDrawingItemIndex = this.drawingData.items.length - 1;
      this.handleDrawResult(newItem.onMouseDown(this.getCanvasRelativeX(event), this.getCanvasRelativeY(event)));
    } else if (this.selectedDrawingItem?.state === 'IN_PROGRESS') {
      this.handleDrawResult(this.selectedDrawingItem.onMouseDown(this.getCanvasRelativeX(event), this.getCanvasRelativeY(event)));
    }
  }

  
  
  mouseMove(event: MouseEvent) {
    if (this.isPanning) {
      const dx = event.clientX - this.panLastX;
      const dy = event.clientY - this.panLastY;
      this.panLastX = event.clientX;
      this.panLastY = event.clientY;
      this.translateX += dx;
      this.translateY += dy;
      this.applyZoom();
      event.preventDefault();
      return;
    }
    if (this.selectedDrawingItem?.state === 'IN_PROGRESS') {
      this.handleDrawResult(this.selectedDrawingItem.onMouseMove(this.getCanvasRelativeX(event), this.getCanvasRelativeY(event)));
    }
  }

  mouseUp(event: MouseEvent): void {
    if (this.isPanning) {
      this.isPanning = false;
      return;
    }
    if (!this.isDrawing) { return; }
    const item = this.selectedDrawingItem;
    if (this.selectedDrawingItem?.state === 'IN_PROGRESS') {
      this.handleDrawResult(item.onMouseUp(this.getCanvasRelativeX(event), this.getCanvasRelativeY(event)));
    }
  }

  mouseDbClick(event: MouseEvent) {
    let newItem: DrawingDataItem;
    if (! this.isDrawing) {
      switch (this.selectedElementValue) {
        case 'cross':
        case 'triangle':
        case 'square':
        case 'circle':
          newItem = DrawingDataItemBuilder.create(this.selectedElementValue, this.selectedSubElementValue);
      }
    }
    if (newItem) {
      newItem.state = 'DONE';
      newItem.common = this.buildCommonDrawingElementData();
      this.drawingData.items.push(newItem);
      this.selectedDrawingItemIndex = this.drawingData.items.length - 1;
      this.handleDrawResult(newItem.onMouseDbClick(this.getCanvasRelativeX(event), this.getCanvasRelativeY(event)));
    } else if (this.selectedDrawingItem) {
      this.handleDrawResult(this.selectedDrawingItem.onMouseDbClick(this.getCanvasRelativeX(event), this.getCanvasRelativeY(event)));
    }
  }

  handleDrawResult(result: DrawingDataItemDrawResult) {
    if (result) {
      if (result.changed) {
        this.refresh();
      }
      // console.log(result, this.selectedDrawingItem)
      const selected = this.selectedDrawingItem;
      if (!selected) {
        this.isDrawing = false;
      } else {
        this.isDrawing = result.stillDrawing;
        selected.state = this.isDrawing ? 'IN_PROGRESS' : 'DONE';
        if (!this.isDrawing){
          this.sendOnChangedEvent();
        }
      }
      this.resetVertexIndexToMove();
    }
  }

  stopDrawing(item: DrawingDataItem) {
    if (item.state === 'IN_PROGRESS') {
      item.state = 'DONE';
    }
    this.isDrawing = false;
    this.sendOnChangedEvent();
  }

  buildCommonDrawingElementData(): DrawingDataItemCommon {
    return {
      color: this.formGroup.value.color,
      thickness: this.formGroup.value.thickness,
      size: this.formGroup.value.size,
      label: this.formGroup.value.label,
      name: this.formGroup.value.name
    };
  }

  refresh() {
    if (this.imageUrl) {
      if (this.drawingCanvasRef && this.drawingCtx) {
        const canvas = this.drawingCanvasRef.nativeElement;
        this.drawingCtx.clearRect(0, 0, canvas.width, canvas.height);
        this.drawingData?.items.forEach((item: DrawingDataItem) => {
          item.draw(this.drawingCtx);
        });
      }
    }
  }

  sendOnChangedEvent() {
    this.changedEvent.emit({
      image: this.drawingCanvasRef?.nativeElement?.toDataURL(),
      data: this.drawingData
    })
  }

  getCanvasRelativeX(event: MouseEvent) {
    const canvas = this.drawingCanvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    return (event.clientX - rect.left) * scaleX;
  }

  getCanvasRelativeY(event: MouseEvent) {
    const canvas = this.drawingCanvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleY = canvas.height / rect.height;
    return (event.clientY - rect.top) * scaleY;
  }

  selectElement(selectedElementValue: string) {
    this.selectedElementValue = selectedElementValue;
    this.selectedSubElementValue = undefined;
    if (this.selectedDrawingElement?.subElements?.length > 0) {
      this.selectedSubElementValue = this.selectedDrawingElement.subElements[0].value;
    }
  }

  selectSubTypeElement(selectedSubElementValue: string) {
    console.log('selectSubTypeElement', selectedSubElementValue, this.selectedDrawingElement, this.selectedDrawingElement?.subElements.findIndex(value => value.value === this.selectedSubElementValue));
    this.selectedSubElementValue = selectedSubElementValue;
    if (this.selectedDrawingElement && this.selectedDrawingElement?.subElements.findIndex(value => value.value === this.selectedSubElementValue) > -1) {
      console.log(this.selectedDrawingItem, this.selectedElementValue);
      if (this.selectedDrawingItem?.typeValue === this.selectedElementValue){
        this.selectedDrawingItem.subTypeValue = this.selectedSubElementValue;
        this.refresh();
        this.sendOnChangedEvent();
      }
    }
  }

  moveX(deltaX: number) {
    if (this.selectedDrawingItem) {
      console.log('moveX', this.formGroup.get('vertexIndexToMove').value);
      this.selectedDrawingItem?.moveX(deltaX, this.formGroup.get('vertexIndexToMove').value);
      this.refresh();
      this.sendOnChangedEvent();
    }
  }

  moveY(deltaY: number) {
    if (this.selectedDrawingItem) {
      this.selectedDrawingItem?.moveY(deltaY, this.formGroup.get('vertexIndexToMove').value);
      this.refresh();
      this.sendOnChangedEvent();
    }
  }

  changeSize(deltaPercentage: number) {
    if (this.drawingCanvasRef) {

    }
    let delta = Math.round(this.drawingCanvasRef.nativeElement.width * deltaPercentage);

    let size = this.formGroup.get('size').value;
    size += delta;
    if (size < 0){
      size = 1;
    }
    this.formGroup.get('size').setValue(size);
    this.refreshOptions();
  }

  changeThickness(delta: number) {
    let thickness = this.formGroup.get('thickness').value;
    thickness += delta;
    if (thickness < 0){
      thickness = 1;
    }
    this.formGroup.get('thickness').setValue(thickness);
    this.refreshOptions();
  }



  selectItem(item: DrawingDataItem) {
    if (item) {
      if (this.selectedDrawingItem?.state === 'IN_PROGRESS') {
        this.drawingData.items = this.drawingData.items.splice(this.selectedDrawingItemIndex, 1);
      }

      this.selectedDrawingItemIndex = this.drawingData.items.indexOf(item);
      if (this.selectedDrawingItemIndex > -1) {
        this.formGroup.patchValue({
          color: item.common.color,
          thickness: item.common.thickness,
          size: item.common.size,
          label: item.common.label,
          name: item.common.name
        })
      }
      this.isDrawing = false;
    }

  }

  resetVertexIndexToMove() {
    this.optionVertexElements = [];
    if (this.selectedDrawingItem) {
      this.optionVertexElements.push(new SelectElementV2<number>(-1, 'Tous les points', -1));
      if (this.selectedDrawingItem.getPointsToMove()?.length > 1) {
        let display = 1;
        console.log(this.selectedDrawingItem.getPointsToMove());
        for (let i = 0; i < this.selectedDrawingItem.getPointsToMove().length; i++) {
          if (this.selectedDrawingItem.getPointsToMove()[i].visibleForUser) {
            this.optionVertexElements.push(new SelectElementV2<number>(i, 'Point ' + (display++), i));
          }
        }
      }
      this.formGroup.get('vertexIndexToMove').setValue(-1);
    }
  }

  deleteItem(item: DrawingDataItem) {
    this.drawingData.items.splice(this.drawingData.items.indexOf(item), 1);
    this.refresh();
    this.checkDrawingStillCorrect();
    this.sendOnChangedEvent();
  }

  checkDrawingStillCorrect() {
    if (this.drawingData.items.length === 0) {
      this.isDrawing = false;
    }
  }

  get selectedDrawingItem(): DrawingDataItem {
    return this.selectedDrawingItemIndex > -1 ? this.drawingData.items[this.selectedDrawingItemIndex] : undefined;
  }

  get selectedDrawingElement(): DrawingElement {
    return this.selectedElementValue ? this.drawingElements.find(value => value.value === this.selectedElementValue) : undefined;
  }
}

export class DrawingOptions {
  options: DrawingOption[] = [];
}

export class DrawingOption {
  name: string;
  label: string;
  element: string;
  subType: string;
  size: number;
  thickness: number;
  color: string;
}

export class DrawingOnImageChangedEvent {
  image: string;
  data: DrawingData;
}
